import { enrichFirstFourWithOdds } from "@/utils/early-mail-prices";
import { useCallback, useEffect, useMemo, useState } from "react";
import { addDays, format, parse } from "date-fns";
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight } from "lucide-react";
import { fetchFutureRacesForDate, useFutureTipsQuery } from "@/api/vercel";
import { Spinner } from "@/components/ui/spinner";
import { usePaidUser } from "@/hooks/usePaidUser";
import { extractFutureTipsPayload } from "@/utils/future-tips-utils";
import { logger } from "@/utils/logger";
import { buildEarlyMailAssessmentHtml } from "@/utils/render-tips";

const normalizeTrackKeyPart = (trackName) =>
  `${trackName || ""}`.trim().replace(/\s+/g, "-");

const normalizeTrackDisplayName = (trackName) =>
  `${trackName || ""}`.trim().replace(/-/g, " ");

const normalizeHorseName = (name) =>
  `${name || ""}`.toLowerCase().replace(/\s+/g, " ").trim();

const parseBookEntry = (line) => {
  if (typeof line !== "string") return null;
  const parts = line.split(":");
  if (parts.length < 2) return null;

  const left = parts[0].trim();
  const right = parts.slice(1).join(":").trim();
  const price = parseFloat(right);
  if (!Number.isFinite(price)) return null;

  const dotIdx = left.indexOf(".");
  const number = dotIdx >= 0 ? left.slice(0, dotIdx).trim() : "";
  const name = dotIdx >= 0 ? left.slice(dotIdx + 1).trim() : left.trim();
  const formattedName = number ? `${number}. ${name}` : left;

  return {
    formattedName,
    number,
    name,
    currentPrice: price,
  };
};

const normalizeFormattedNameKey = (formattedName) =>
  `${formattedName || ""}`.toLowerCase().replace(/\s+/g, "").replace(/\./g, "").trim();

const normalizeHorseKey = ({ formattedName, number, name }) => {
  const formatted = `${formattedName || ""}`.trim();
  const dotIdx = formatted.indexOf(".");
  const numberFromFormatted = dotIdx >= 0 ? formatted.slice(0, dotIdx).trim() : "";
  const nameFromFormatted = dotIdx >= 0 ? formatted.slice(dotIdx + 1).trim() : "";
  const normalizedNumber = `${number || numberFromFormatted || ""}`.trim().replace(/^0+/, "");
  const normalizedName = normalizeHorseName(name || nameFromFormatted || "");
  if (!normalizedNumber && !normalizedName) return "";
  return `${normalizedNumber}|${normalizedName}`;
};

const buildFullBookEntries = (race) => {
  const bookLines = Array.isArray(race?.book) && race.book.length
    ? race.book
    : Array.isArray(race?.calculatedOdds) && race.calculatedOdds.length
      ? race.calculatedOdds
      : [];

  if (!bookLines.length) {
    return Array.isArray(race?.firstFourData) ? race.firstFourData : [];
  }

  const parsed = bookLines.map(parseBookEntry).filter(Boolean);
  if (!parsed.length) {
    return Array.isArray(race?.firstFourData) ? race.firstFourData : [];
  }

  const firstFour = Array.isArray(race?.firstFourData) ? race.firstFourData : [];
  if (!firstFour.length) return parsed;

  const byNumber = new Map();
  const byName = new Map();
  const byFormattedName = new Map();
  const byHorseKey = new Map();
  const rankByNumber = new Map();
  const rankByName = new Map();
  firstFour.forEach((entry, index) => {
    const formatted = `${entry?.formattedName || ""}`.trim();
    const dotIdx = formatted.indexOf(".");
    const numberFromFormatted = dotIdx >= 0 ? formatted.slice(0, dotIdx).trim() : "";
    const nameFromFormatted = dotIdx >= 0 ? formatted.slice(dotIdx + 1).trim() : "";
    const number = `${entry?.number || numberFromFormatted || ""}`.trim();
    const name = normalizeHorseName(entry?.name || nameFromFormatted || "");
    const horseKey = normalizeHorseKey(entry);
    const formattedKey = normalizeFormattedNameKey(entry?.formattedName);

    if (number) byNumber.set(number, entry);
    if (name) byName.set(name, entry);
    if (formattedKey) byFormattedName.set(formattedKey, entry);
    if (horseKey) byHorseKey.set(horseKey, entry);
    if (number) rankByNumber.set(number, index + 1);
    if (name) rankByName.set(name, index + 1);
  });

  return parsed.map((entry) => {
    const numberKey = `${entry?.number || ""}`.trim();
    const nameKey = normalizeHorseName(entry?.name || "");
    const formattedKey = normalizeFormattedNameKey(entry?.formattedName);
    const horseKey = normalizeHorseKey(entry);
    const match =
      (formattedKey && byFormattedName.get(formattedKey)) ||
      (horseKey && byHorseKey.get(horseKey)) ||
      (numberKey && byNumber.get(numberKey)) ||
      (nameKey && byName.get(nameKey)) ||
      null;
    const lexiProRank =
      (numberKey && rankByNumber.get(numberKey)) ||
      (nameKey && rankByName.get(nameKey)) ||
      null;

    return match
      ? {
          ...match,
          ...entry,
          currentPrice: entry.currentPrice,
          isLexiProTip: true,
          lexiProRank,
        }
      : { ...entry, isLexiProTip: false, lexiProRank: null };
  });
};

const parseHorseNumberFromFormattedName = (formattedName) => {
  if (typeof formattedName !== "string") return "";
  const trimmed = formattedName.trim();
  if (!trimmed) return "";
  const dotIdx = trimmed.indexOf(".");
  const candidate = dotIdx >= 0 ? trimmed.slice(0, dotIdx) : trimmed;
  return candidate.trim().replace(/^0+/, "") || candidate.trim();
};

const parseHorseNameFromFormattedName = (formattedName) => {
  if (typeof formattedName !== "string") return "";
  const trimmed = formattedName.trim();
  if (!trimmed) return "";
  const dotIdx = trimmed.indexOf(".");
  if (dotIdx < 0) return trimmed;
  return trimmed.slice(dotIdx + 1).trim();
};

const parseOddsValue = (value) => {
  const parsed =
    typeof value === "number"
      ? value
      : parseFloat(String(value || "").replace(/[^0-9.\-]/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeAnalysisText = (text) =>
  `${text || ""}`.toLowerCase().replace(/\s+/g, " ").trim();

const isHorseMentionedInAnalysis = (analysisText, horseNumber, horseName) => {
  const normalizedAnalysis = normalizeAnalysisText(analysisText);
  if (!normalizedAnalysis) return false;

  const normalizedName = normalizeAnalysisText(horseName);
  if (normalizedName) {
    const namePattern = new RegExp(`\\b${normalizedName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "i");
    if (namePattern.test(normalizedAnalysis)) return true;
  }

  const normalizedNumber = `${horseNumber || ""}`.trim().replace(/^0+/, "");
  if (!normalizedNumber) return false;

  const numberPattern = new RegExp(`(?:^|\\s|\\()${normalizedNumber}(?:\\.|\\s|\\)|$)`, "i");
  return numberPattern.test(normalizedAnalysis) || normalizedAnalysis.includes(`#${normalizedNumber}`);
};

const stateOrder = {
  VIC: 1,
  NSW: 2,
  QLD: 3,
  SA: 4,
  WA: 5,
  TAS: 6,
  NT: 7,
  ACT: 8,
  Other: 9,
};

const popularMetroTracks = {
  VIC: ["Flemington", "Caulfield", "Moonee Valley", "Sandown", "Mornington"],
  NSW: ["Randwick", "Rosehill", "Warwick Farm", "Canterbury", "Kembla Grange"],
  QLD: ["Eagle Farm", "Doomben", "Gold Coast", "Sunshine Coast", "Ipswich"],
  SA: ["Morphettville", "Murray Bridge", "Gawler", "Port Augusta"],
  WA: ["Ascot", "Belmont", "Pinjarra", "Bunbury"],
  TAS: ["Hobart", "Launceston", "Devonport"],
  NT: ["Darwin", "Alice Springs"],
};

const sortTracksByPopularityAndState = (tracksArray) => {
  const globalMetroPriority = [];
  Object.keys(stateOrder).forEach((state) => {
    if (popularMetroTracks[state]) {
      popularMetroTracks[state].forEach((track) => {
        globalMetroPriority.push(track);
      });
    }
  });

  const isMetroTrack = (track) => {
    const state = track.state || "Other";
    const metroList = popularMetroTracks[state] || [];
    return metroList.some(
      (metroTrack) =>
        track.name === metroTrack ||
        (metroTrack === "Moonee Valley" && track.name.includes("Moonee")),
    );
  };

  const getGlobalMetroPriority = (track) => {
    const exactIndex = globalMetroPriority.findIndex((m) => track.name === m);
    if (exactIndex !== -1) return exactIndex;

    if (track.name.includes("Moonee")) {
      const mooneeIndex = globalMetroPriority.findIndex((m) => m === "Moonee Valley");
      if (mooneeIndex !== -1) return mooneeIndex;
    }

    return globalMetroPriority.length;
  };

  return tracksArray.sort((a, b) => {
    const isMetroA = isMetroTrack(a);
    const isMetroB = isMetroTrack(b);

    if (isMetroA && isMetroB) {
      return getGlobalMetroPriority(a) - getGlobalMetroPriority(b);
    }

    if (isMetroA && !isMetroB) return -1;
    if (!isMetroA && isMetroB) return 1;

    const stateA = a.state || "Other";
    const stateB = b.state || "Other";

    if (stateA !== stateB) {
      return stateOrder[stateA] - stateOrder[stateB];
    }

      return a.name.localeCompare(b.name);
  });
};

export default function VipPage() {
  const isPaidUser = usePaidUser();
  const [trackData, setTrackData] = useState({});

  const baseDate = useMemo(() => new Date(), []);

  const { date0, date1, date2, date3 } = useMemo(() => {
    return {
      date0: format(baseDate, "dd-MM-yyyy"),
      date1: format(addDays(baseDate, 1), "dd-MM-yyyy"),
      date2: format(addDays(baseDate, 2), "dd-MM-yyyy"),
      date3: format(addDays(baseDate, 3), "dd-MM-yyyy"),
    };
  }, [baseDate]);

  const futureTips0 = useFutureTipsQuery({ date: date0, enabled: isPaidUser });
  const futureTips1 = useFutureTipsQuery({ date: date1, enabled: isPaidUser });
  const futureTips2 = useFutureTipsQuery({ date: date2, enabled: isPaidUser });
  const futureTips3 = useFutureTipsQuery({ date: date3, enabled: isPaidUser });

  const isFutureTipsLoading =
    futureTips0.isLoading ||
    futureTips1.isLoading ||
    futureTips2.isLoading ||
    futureTips3.isLoading;
  const [pastEarlyMailDays, setPastEarlyMailDays] = useState([]);
  const [pastLoadOffset, setPastLoadOffset] = useState(1);
  const [isPastLoading, setIsPastLoading] = useState(false);
  const [pastLoadMessage, setPastLoadMessage] = useState("");
  const [pastLoadMessageType, setPastLoadMessageType] = useState("info");

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDateFilter, setSelectedDateFilter] = useState("all");
  const [selectedTrackFilter, setSelectedTrackFilter] = useState("all");
  const [selectedRaceFilter, setSelectedRaceFilter] = useState("all");
  const [sortConfig, setSortConfig] = useState({
    key: "odds",
    direction: "asc",
  });
  const [showOddsPercent, setShowOddsPercent] = useState(false);
  const [collapsedDays, setCollapsedDays] = useState({});
  const [collapsedTracks, setCollapsedTracks] = useState({});
  const [collapsedRaces, setCollapsedRaces] = useState({});
  const [dayToggleMode, setDayToggleMode] = useState({});
  const getDayToggleMode = (dayKey) => dayToggleMode?.[dayKey] || "expand-day";

  const nextPastDate = useMemo(
    () => format(addDays(baseDate, -pastLoadOffset), "dd-MM-yyyy"),
    [baseDate, pastLoadOffset],
  );

  const nextPastDateLabel = useMemo(() => {
    const parsedDate = parse(nextPastDate, "dd-MM-yyyy", new Date());
    return format(parsedDate, "EEE d MMM");
  }, [nextPastDate]);

  const dayToggleOrder = [
    "expand-day",
    "expand-tracks",
    "expand-races",
    "expand-all",
  ];

  const toggleDayButton = (dayKey, tracks) => {
    if (!dayKey) return;
    const current = getDayToggleMode(dayKey);

    if (current === "expand-day") {
      setCollapsedDays((prev) => ({ ...prev, [dayKey]: false }));
      setAllTracksCollapsedForDay(tracks, true);
      setAllRacesCollapsedForDay(tracks, true);
      setDayToggleMode((prev) => ({ ...prev, [dayKey]: "expand-tracks" }));
    } else if (current === "expand-tracks") {
      setCollapsedDays((prev) => ({ ...prev, [dayKey]: false }));
      setAllTracksCollapsedForDay(tracks, false);
      setAllRacesCollapsedForDay(tracks, true);
      setDayToggleMode((prev) => ({ ...prev, [dayKey]: "expand-races" }));
    } else if (current === "expand-races") {
      setCollapsedDays((prev) => ({ ...prev, [dayKey]: false }));
      setAllTracksCollapsedForDay(tracks, false);
      setAllRacesCollapsedForDay(tracks, false);
      setDayToggleMode((prev) => ({ ...prev, [dayKey]: "expand-all" }));
    } else if (current === "expand-all") {
      setCollapsedDays((prev) => ({ ...prev, [dayKey]: false }));
      setAllTracksCollapsedForDay(tracks, false);
      setAllRacesCollapsedForDay(tracks, false);
      setDayToggleMode((prev) => ({ ...prev, [dayKey]: "expand-day" }));
    }

    const nextMode = (() => {
      const idx = dayToggleOrder.indexOf(current);
      const nextIdx = idx === -1 ? 0 : (idx + 1) % dayToggleOrder.length;
      return dayToggleOrder[nextIdx];
    })();

    console.debug("[VIP] day toggle", {
      dayKey,
      from: current,
      to: nextMode,
      tracks: tracks?.length || 0,
    });
  };

  useEffect(() => {
    if (Object.keys(trackData).length > 0) return;
    fetch("/data/horseracing/tracks/track-names.json")
      .then((response) => response.json())
      .then((data) => {
        setTrackData(data);
      })
      .catch((error) => {
        logger.error("Error loading track data:", error);
      });
  }, [trackData]);

  const getTrackState = (trackName) => {
    const match = trackName.match(/\(([^)]+)\)/);
    return match ? match[1] : "";
  };

  const getTrackStateFromData = useCallback(
    (trackName, currentTrackData = trackData) => {
      if (!trackName) return "";
      const cleanTrackName = trackName.replace(/\s*\([^)]*\)\s*$/, "").trim();

      if (currentTrackData && currentTrackData[cleanTrackName]) {
        return currentTrackData[cleanTrackName].State || "";
      }

      if (currentTrackData) {
        const trackKeys = Object.keys(currentTrackData);
        for (const key of trackKeys) {
          if (cleanTrackName.includes(key) || key.includes(cleanTrackName)) {
            return currentTrackData[key].State || "";
          }

          if (
            currentTrackData[key]["alt"] &&
            (cleanTrackName.includes(currentTrackData[key]["alt"]) ||
              currentTrackData[key]["alt"].includes(cleanTrackName))
          ) {
            return currentTrackData[key].State || "";
          }
        }
      }

      const stateFromParens = getTrackState(trackName);
      return stateFromParens || "";
    },
    [trackData],
  );

  const buildTracksArrayFromFuturePayload = useCallback(
    (payload, dateStr) => {
      const tracks = Array.isArray(payload?.tracks) ? payload.tracks : [];
      if (!tracks.length) return [];

      const newTrackMap = new Map();
      const raceDateObj = parse(dateStr, "dd-MM-yyyy", new Date());

      tracks.forEach((t) => {
        const rawTrackName = t?.trackName || t?.track || "";
        const trackDisplayName = normalizeTrackDisplayName(rawTrackName);
        const trackKeyPart = normalizeTrackKeyPart(rawTrackName);

        const races = Array.isArray(t?.races) ? t.races : [];
        if (!races.length) return;

        if (!newTrackMap.has(trackDisplayName)) {
          const trackState = getTrackStateFromData(trackDisplayName, trackData);
          newTrackMap.set(trackDisplayName, {
            name: trackDisplayName,
            races: new Map(),
            state: trackState || "",
            topTips: 0,
          });
        }

        const trackEntry = newTrackMap.get(trackDisplayName);

        races.forEach((r) => {
          const raceNumberRaw = r?.race ?? r?.raceNumber ?? r?.number ?? r?.id;
          const raceNumber = `${raceNumberRaw || ""}`.toString();
          if (!raceNumber) return;

          const raceKey = `${trackKeyPart}-${raceNumber}-${dateStr}`;
          const fullBookData = buildFullBookEntries(r);

          const normalizedRaceData = enrichFirstFourWithOdds({
            ...r,
            track: trackDisplayName,
            race: parseInt(raceNumber, 10) || raceNumber,
            date: dateStr,
            firstFourData: fullBookData,
          });

          if (!trackEntry.races.has(raceNumber)) {
            trackEntry.races.set(raceNumber, {
              number: raceNumber,
              data: normalizedRaceData,
              date: raceDateObj,
              key: raceKey,
              isTopTip: false,
            });
          }
        });
      });

      const tracksArray = Array.from(newTrackMap.values());
      tracksArray.forEach((track) => {
        if (!track.state || track.state === "") {
          track.state = getTrackStateFromData(track.name, trackData) || "";
        }
        track.races = Array.from(track.races.values()).sort(
          (a, b) => parseInt(a.number, 10) - parseInt(b.number, 10),
        );
      });

      return sortTracksByPopularityAndState(tracksArray);
    },
    [getTrackStateFromData, trackData],
  );

  const handleLoadPreviousDay = useCallback(async () => {
    if (!isPaidUser || isPastLoading) return;

    const dateToLoad = nextPastDate;
    setIsPastLoading(true);
    setPastLoadMessage("");
    setPastLoadMessageType("info");

    try {
      const data = await fetchFutureRacesForDate(dateToLoad);
      const payload = extractFutureTipsPayload(data);
      const tracksArray = buildTracksArrayFromFuturePayload(payload, dateToLoad);

      if (Array.isArray(tracksArray) && tracksArray.length > 0) {
        setPastEarlyMailDays((prev) => {
          if (prev.some((day) => day.date === dateToLoad)) return prev;
          return [{ date: dateToLoad, tracks: tracksArray }, ...prev];
        });
      } else {
        setPastLoadMessage(`No Early Mail found for ${dateToLoad}.`);
        setPastLoadMessageType("info");
      }
    } catch (error) {
      logger.error("Error loading past Early Mail:", error);
      setPastLoadMessage("Unable to load previous Early Mail right now.");
      setPastLoadMessageType("error");
    } finally {
      setPastLoadOffset((prev) => prev + 1);
      setIsPastLoading(false);
    }
  }, [
    isPaidUser,
    isPastLoading,
    nextPastDate,
    buildTracksArrayFromFuturePayload,
  ]);

  const futureTipsDays = useMemo(() => {
    if (!isPaidUser) {
      return [];
    }

    const queries = [
      { data: futureTips0.data, isSuccess: futureTips0.isSuccess, date: date0 },
      { data: futureTips1.data, isSuccess: futureTips1.isSuccess, date: date1 },
      { data: futureTips2.data, isSuccess: futureTips2.isSuccess, date: date2 },
      { data: futureTips3.data, isSuccess: futureTips3.isSuccess, date: date3 },
    ];

    return queries
      .filter(({ isSuccess, data }) => isSuccess && data)
      .map(({ data, date }) => {
        const payload = extractFutureTipsPayload(data);
        const tracksArray = buildTracksArrayFromFuturePayload(payload, date);
        return { date, tracks: tracksArray };
      })
      .filter((day) => Array.isArray(day.tracks) && day.tracks.length > 0);
  }, [
    isPaidUser,
    futureTips1.data,
    futureTips2.data,
    futureTips3.data,
    futureTips0.data,
    futureTips1.isSuccess,
    futureTips2.isSuccess,
    futureTips3.isSuccess,
    futureTips0.isSuccess,
    date0,
    date1,
    date2,
    date3,
    buildTracksArrayFromFuturePayload,
  ]);

  const allEarlyMailDays = useMemo(() => {
    const combined = [...pastEarlyMailDays, ...futureTipsDays];
    const byDate = new Map();
    combined.forEach((day) => {
      if (!day?.date || !Array.isArray(day?.tracks)) return;
      if (!byDate.has(day.date)) {
        byDate.set(day.date, day);
      }
    });
    return Array.from(byDate.values());
  }, [pastEarlyMailDays, futureTipsDays]);

  const rawDayGroups = useMemo(() => {
    if (!Array.isArray(allEarlyMailDays)) return [];

    return allEarlyMailDays.map((day) => {
      const dateObj = parse(day.date, "dd-MM-yyyy", new Date());
      const dayName = format(dateObj, "EEEE");
      const races = [];

      (day.tracks || []).forEach((track) => {
        (track.races || []).forEach((race) => {
          const entries = Array.isArray(race?.data?.firstFourData)
            ? race.data.firstFourData
            : [];
          const analysis =
            race?.data?.assessment ||
            race?.data?.decisionExplanation ||
            race?.data?.analysis ||
            "";

          const horses = entries.map((entry, idx) => {
            const formatted = entry?.formattedName || "";
            const horseNumber =
              `${entry?.number || parseHorseNumberFromFormattedName(formatted) || ""}`.trim();
            const horseName = `${entry?.name || parseHorseNameFromFormattedName(formatted) || ""}`.trim();
            const oddsValue = parseOddsValue(
              entry?.currentPrice ?? entry?.lastPriceTraded ?? entry?.effectivePrice,
            );

            return {
              id: `${day.date}-${track.name}-${race.number}-${horseNumber || idx}`,
              date: day.date,
              dayName,
              track: track.name,
              race: race.number,
              horseNumber,
              horseName,
              odds: oddsValue,
              rowIndex: idx + 1,
              isLexiProTip: Boolean(entry?.isLexiProTip),
              lexiProRank: entry?.lexiProRank ?? null,
            };
          });

          races.push({
            id: `${day.date}-${track.name}-${race.number}`,
            date: day.date,
            dayName,
            track: track.name,
            race: race.number,
            analysis,
            analysisHtml: buildEarlyMailAssessmentHtml(analysis),
            horses,
          });
        });
      });

      return {
        date: day.date,
        dayName,
        races,
      };
    });
  }, [allEarlyMailDays]);

  const dateOptions = useMemo(() => {
    const dates = Array.from(new Set(rawDayGroups.map((group) => group.date)));
    return dates.sort((a, b) => parse(a, "dd-MM-yyyy", new Date()) - parse(b, "dd-MM-yyyy", new Date()));
  }, [rawDayGroups]);

  const trackOptions = useMemo(() => {
    const tracks = [];
    rawDayGroups.forEach((group) =>
      group.races.forEach((race) => {
        if (race.track) tracks.push(race.track);
      }),
    );
    return Array.from(new Set(tracks)).sort((a, b) => a.localeCompare(b));
  }, [rawDayGroups]);

  const raceOptions = useMemo(() => {
    const races = [];
    rawDayGroups.forEach((group) =>
      group.races.forEach((race) => {
        if (race.race !== null && race.race !== undefined) {
          races.push(String(race.race));
        }
      }),
    );
    return Array.from(new Set(races))
      .filter(Boolean)
      .sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
  }, [rawDayGroups]);

  const groupedRows = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const multiplier = sortConfig.direction === "asc" ? 1 : -1;

    const sortHorses = (horses) => {
      const sorted = [...horses];
      const { key } = sortConfig;

      sorted.sort((a, b) => {
        if (key === "row") {
          return (a.rowIndex - b.rowIndex) * multiplier;
        }

        if (key === "horseNumber") {
          const numA = parseInt(a.horseNumber || "0", 10);
          const numB = parseInt(b.horseNumber || "0", 10);
          if (Number.isNaN(numA) && Number.isNaN(numB)) return 0;
          if (Number.isNaN(numA)) return 1 * multiplier;
          if (Number.isNaN(numB)) return -1 * multiplier;
          return (numA - numB) * multiplier;
        }

        if (key === "horseName") {
          return a.horseName.localeCompare(b.horseName) * multiplier;
        }

        if (key === "odds") {
          const oddsA = a.odds ?? Number.POSITIVE_INFINITY;
          const oddsB = b.odds ?? Number.POSITIVE_INFINITY;
          return (oddsA - oddsB) * multiplier;
        }

        if (key === "date") {
          return (
            (parse(a.date, "dd-MM-yyyy", new Date()) -
              parse(b.date, "dd-MM-yyyy", new Date())) * multiplier
          );
        }

        if (key === "track") {
          return a.track.localeCompare(b.track) * multiplier;
        }

        if (key === "race") {
          return (parseInt(a.race, 10) - parseInt(b.race, 10)) * multiplier;
        }

        return 0;
      });

      return sorted;
    };

    const sortRaces = (races) => {
      const { key } = sortConfig;
      if (key !== "track" && key !== "race") return races;
      const sorted = [...races];

      sorted.sort((a, b) => {
        if (key === "track") {
          const trackDiff = a.track.localeCompare(b.track) * multiplier;
          if (trackDiff !== 0) return trackDiff;
          return (parseInt(a.race, 10) - parseInt(b.race, 10)) * multiplier;
        }
        return (parseInt(a.race, 10) - parseInt(b.race, 10)) * multiplier;
      });

      return sorted;
    };

    const sortTracks = (tracks) => {
      const { key } = sortConfig;
      if (key !== "track") return tracks;
      const sorted = [...tracks];
      sorted.sort((a, b) => a.track.localeCompare(b.track) * multiplier);
      return sorted;
    };

    const filteredGroups = rawDayGroups
      .map((group) => {
        if (selectedDateFilter !== "all" && group.date !== selectedDateFilter) {
          return null;
        }

        const races = group.races
          .filter((race) => {
            if (selectedTrackFilter !== "all" && race.track !== selectedTrackFilter) {
              return false;
            }
            if (selectedRaceFilter !== "all" && String(race.race) !== selectedRaceFilter) {
              return false;
            }
            return true;
          })
          .map((race) => {
            const horses = race.horses.filter((row) => {
              if (!normalizedSearch) return true;

              const haystack = [
                row.horseName,
                row.horseNumber,
                row.track,
                row.date,
                row.dayName,
                row.race,
              ]
                .map((value) => String(value || "").toLowerCase())
                .join(" ");

              return haystack.includes(normalizedSearch);
            });

            const enrichedHorses = horses.map((row) => ({
              ...row,
              isMentioned: isHorseMentionedInAnalysis(
                race.analysis || "",
                row.horseNumber,
                row.horseName,
              ),
            }));

            return {
              ...race,
              horses: sortHorses(enrichedHorses),
            };
          })
          .filter((race) => race.horses.length > 0);

        if (races.length === 0) return null;

        const trackMap = new Map();
        races.forEach((race) => {
          if (!trackMap.has(race.track)) {
            trackMap.set(race.track, {
              key: `${group.date}-${race.track}`,
              track: race.track,
              races: [],
            });
          }
          trackMap.get(race.track).races.push(race);
        });

        const tracks = sortTracks(
          Array.from(trackMap.values()).map((trackGroup) => ({
            ...trackGroup,
            races: sortRaces(trackGroup.races),
          })),
        );

        return {
          ...group,
          tracks,
        };
      })
      .filter(Boolean);

    if (sortConfig.key === "date") {
      filteredGroups.sort((a, b) => {
        const dateDiff =
          parse(a.date, "dd-MM-yyyy", new Date()) -
          parse(b.date, "dd-MM-yyyy", new Date());
        return dateDiff * multiplier;
      });
    }

    return filteredGroups;
  }, [
    rawDayGroups,
    searchTerm,
    selectedDateFilter,
    selectedTrackFilter,
    selectedRaceFilter,
    sortConfig,
  ]);

  const totalFilteredTips = useMemo(() => {
    return groupedRows.reduce(
      (count, group) =>
        count +
        group.tracks.reduce(
          (sum, trackGroup) =>
            sum + trackGroup.races.reduce((acc, race) => acc + race.horses.length, 0),
          0,
        ),
      0,
    );
  }, [groupedRows]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key, direction: "asc" };
    });
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "asc" ? (
      <ArrowUp className="w-3.5 h-3.5 text-gray-500" aria-hidden="true" />
    ) : (
      <ArrowDown className="w-3.5 h-3.5 text-gray-500" aria-hidden="true" />
    );
  };

  const formatOddsDisplay = (oddsValue, asPercent) => {
    if (!oddsValue || !Number.isFinite(oddsValue) || oddsValue <= 0) return "-";
    if (asPercent) {
      const pct = 100 / oddsValue;
      return `${pct.toFixed(1)}%`;
    }
    return `$${oddsValue.toFixed(2)}`;
  };

  const toggleDayCollapse = (dayKey) => {
    if (!dayKey) return;
    setCollapsedDays((prev) => ({ ...prev, [dayKey]: !prev?.[dayKey] }));
  };

  const getSilkSrc = (entry) => {
    if (entry?.smallSilks) return entry.smallSilks;
    if (entry?.silks) return entry.silks;
    if (entry?.horseId) {
      return `/api/images/silks?filename=${entry.horseId}.jpeg`;
    }
    return "/images/silk-fallback.jpg";
  };

  const getSilkDebugInfo = (entry) => {
    const resolved = getSilkSrc(entry);
    return [
      `resolved=${resolved || "none"}`,
      `smallSilks=${entry?.smallSilks || "n/a"}`,
      `silks=${entry?.silks || "n/a"}`,
      `horseId=${entry?.horseId || "n/a"}`,
    ].join(" | ");
  };

  const toggleTrackCollapse = (trackKey) => {
    if (!trackKey) return;
    setCollapsedTracks((prev) => ({ ...prev, [trackKey]: !prev?.[trackKey] }));
  };

  const toggleRaceCollapse = (raceKey) => {
    if (!raceKey) return;
    setCollapsedRaces((prev) => ({ ...prev, [raceKey]: !prev?.[raceKey] }));
  };

  const setAllTracksCollapsedForDay = (tracks, shouldCollapse) => {
    if (!Array.isArray(tracks) || tracks.length === 0) return;
    setCollapsedTracks((prev) => {
      const next = { ...prev };
      tracks.forEach((track) => {
        next[track.key] = shouldCollapse;
      });
      return next;
    });
  };

  const setAllRacesCollapsedForDay = (tracks, shouldCollapse) => {
    if (!Array.isArray(tracks) || tracks.length === 0) return;
    setCollapsedRaces((prev) => {
      const next = { ...prev };
      tracks.forEach((track) => {
        (track.races || []).forEach((race) => {
          next[race.id] = shouldCollapse;
        });
      });
      return next;
    });
  };

  const isEarlyMailLoading = isFutureTipsLoading || isPastLoading;
  const hasEarlyMailDays =
    Array.isArray(allEarlyMailDays) && allEarlyMailDays.length > 0;
  const showEmptyState = !isEarlyMailLoading && !hasEarlyMailDays;

  return (
    <div className="home-page-container">
      <div className="container mx-auto px-4 py-4 bg-white">
        <div className="mb-4">
          <h1 className="text-2xl font-semibold">Early Mail VIP</h1>
        </div>

        {!isPaidUser && (
          <div className="rounded-lg border border-[#fba6e1] bg-[#fba6e1]/10 px-4 py-3 text-sm text-gray-700">
            VIP access is required to view this page.
          </div>
        )}

        {isPaidUser && (
          <>
            <div className="mb-6 rounded-lg border border-gray-200 bg-white shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                <div>
                  <p className="text-sm font-semibold text-gray-700">Load earlier Early Mail</p>
                  <p className="text-xs text-gray-500">
                    Fetch previous days, starting with yesterday.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleLoadPreviousDay}
                  disabled={isPastLoading}
                  className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-1.5 text-xs font-semibold text-gray-700 shadow-sm transition hover:border-gray-300 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isPastLoading ? (
                    <>
                      <Spinner className="h-3.5 w-3.5" />
                      Loading {nextPastDateLabel}
                    </>
                  ) : (
                    <>Load {nextPastDateLabel}</>
                  )}
                </button>
              </div>
              {pastLoadMessage && (
                <div
                  className={`px-4 pb-3 text-xs ${
                    pastLoadMessageType === "error"
                      ? "text-red-600"
                      : "text-gray-500"
                  }`}
                >
                  {pastLoadMessage}
                </div>
              )}
            </div>

            {isEarlyMailLoading && (
              <div className="px-0 pb-6 pt-2">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Spinner className="w-4 h-4" />
                  <span>Loading Early Mail...</span>
                </div>
              </div>
            )}

            {showEmptyState && (
                <div className="px-0 pb-6 pt-2">
                  <div className="text-sm text-gray-500">
                    No Early Mail available at the moment. Please try again after midday or tomorrow.
                  </div>
                </div>
              )}

            {hasEarlyMailDays && (
                <div className="hidden md:block mb-8">
                  <div className="mb-4 rounded-lg border border-gray-200 bg-white shadow-sm">
                    <div className="border-b border-gray-100 px-4 py-3">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div>
                          <h2 className="text-base font-semibold">Early Mail Tips</h2>
                          <p className="text-xs text-gray-500">
                            {totalFilteredTips} tips shown
                          </p>
                          <p className="text-[11px] text-gray-400">
                            Odds generated by Lexi
                          </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search horse, track, race..."
                            className="w-56 rounded-md border border-gray-200 px-3 py-1.5 text-sm"
                          />
                          <select
                            value={selectedDateFilter}
                            onChange={(e) => setSelectedDateFilter(e.target.value)}
                            className="rounded-md border border-gray-200 px-2 py-1.5 text-sm"
                          >
                            <option value="all">All dates</option>
                            {dateOptions.map((date) => (
                              <option key={date} value={date}>
                                {date}
                              </option>
                            ))}
                          </select>
                          <select
                            value={selectedTrackFilter}
                            onChange={(e) => setSelectedTrackFilter(e.target.value)}
                            className="rounded-md border border-gray-200 px-2 py-1.5 text-sm"
                          >
                            <option value="all">All tracks</option>
                            {trackOptions.map((track) => (
                              <option key={track} value={track}>
                                {track}
                              </option>
                            ))}
                          </select>
                          <select
                            value={selectedRaceFilter}
                            onChange={(e) => setSelectedRaceFilter(e.target.value)}
                            className="rounded-md border border-gray-200 px-2 py-1.5 text-sm"
                          >
                            <option value="all">All races</option>
                            {raceOptions.map((race) => (
                              <option key={race} value={race}>
                                Race {race}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            onClick={() => setShowOddsPercent((prev) => !prev)}
                            className="rounded-full border border-gray-200 bg-white px-3 py-1 text-xs font-semibold text-gray-600 shadow-sm hover:border-gray-300"
                          >
                            {showOddsPercent ? "Show $price" : "Show % odds"}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {groupedRows.length === 0 && (
                    <div className="rounded-lg border border-gray-200 bg-white px-4 py-6 text-center text-sm text-gray-500">
                      No tips match your filters.
                    </div>
                  )}

                  {groupedRows.map((group) => {
                    const isDayCollapsed = Boolean(collapsedDays?.[group.date]);
                    const currentDayToggleMode = getDayToggleMode(group.date);
                    const trackCount = Array.isArray(group.tracks) ? group.tracks.length : 0;
                    const raceCount = Array.isArray(group.tracks)
                      ? group.tracks.reduce((sum, track) => sum + track.races.length, 0)
                      : 0;
                    const horseCount = Array.isArray(group.tracks)
                      ? group.tracks.reduce(
                          (sum, track) =>
                            sum + track.races.reduce((acc, race) => acc + race.horses.length, 0),
                          0,
                        )
                      : 0;

                    return (
                      <div
                        key={group.date}
                        className="mb-6 rounded-lg border border-gray-200 bg-[#f9f6ff] shadow-sm"
                      >
                        <div className="border-b border-gray-200 px-4 py-3 flex items-center justify-between">
                          <div className="flex items-center gap-3 flex-wrap">
                            <h3 className="text-sm font-semibold text-gray-700">
                              {group.dayName}
                              <span className="ml-2 text-xs font-medium text-gray-400">
                                {format(parse(group.date, "dd-MM-yyyy", new Date()), "d MMM yyyy")}
                              </span>
                            </h3>
                            <div className="flex flex-wrap gap-2">
                              <span className="rounded-full bg-white/70 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-gray-600">
                                {trackCount} tracks
                              </span>
                              <span className="rounded-full bg-white/70 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-gray-600">
                                {raceCount} races
                              </span>
                              <span className="rounded-full bg-white/70 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-gray-600">
                                {horseCount} horses
                              </span>
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            <button
                              type="button"
                              onClick={() => toggleDayCollapse(group.date)}
                              className="inline-flex items-center gap-1 text-xs font-semibold text-gray-600 hover:text-gray-800"
                            >
                              {isDayCollapsed ? (
                                <>
                                  <ChevronRight className="w-3.5 h-3.5" aria-hidden="true" />
                                  Show {group.dayName}
                                </>
                              ) : (
                                <>
                                  <ChevronDown className="w-3.5 h-3.5" aria-hidden="true" />
                                  Hide {group.dayName}
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                        {!isDayCollapsed && (
                          <div className="space-y-6 p-4">
                            {group.tracks.map((trackGroup) => {
                              const isTrackCollapsed = Boolean(
                                collapsedTracks?.[trackGroup.key],
                              );
                              const trackRaceCount = trackGroup.races.length;
                              const trackHorseCount = trackGroup.races.reduce(
                                (sum, race) => sum + race.horses.length,
                                0,
                              );

                              return (
                                <div key={trackGroup.key} className="rounded-lg border border-gray-200 bg-white">
                                  <div className="border-b border-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 flex items-center justify-between">
                                    <div className="flex items-center gap-3 flex-wrap">
                                      <span>{trackGroup.track}</span>
                                      <div className="flex flex-wrap gap-2">
                                        <span className="rounded-full bg-gray-100 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-gray-600">
                                          {trackRaceCount} races
                                        </span>
                                        <span className="rounded-full bg-gray-100 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-gray-600">
                                          {trackHorseCount} horses
                                        </span>
                                      </div>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => toggleTrackCollapse(trackGroup.key)}
                                      className="inline-flex items-center gap-1 text-xs font-semibold text-gray-600 hover:text-gray-800"
                                    >
                                      {isTrackCollapsed ? (
                                        <>
                                          <ChevronRight className="w-3.5 h-3.5" aria-hidden="true" />
                                          Expand {trackGroup.track}
                                        </>
                                      ) : (
                                        <>
                                          <ChevronDown className="w-3.5 h-3.5" aria-hidden="true" />
                                          Minimise {trackGroup.track}
                                        </>
                                      )}
                                    </button>
                                  </div>
                                  {!isTrackCollapsed && (
                                    <div className="space-y-5 p-4">
                                      {trackGroup.races.map((raceGroup) => {
                                        const isRaceCollapsed = Boolean(
                                          collapsedRaces?.[raceGroup.id],
                                        );
                                        const topLexiTips = raceGroup.horses
                                          .filter((horse) => horse.isLexiProTip)
                                          .sort((a, b) => (a.lexiProRank || 99) - (b.lexiProRank || 99))
                                          .slice(0, 4);
                                        const raceHorseCount = raceGroup.horses.length;

                                        return (
                                          <div key={raceGroup.id} className="rounded-lg border border-gray-200">
                                            <div className="border-b border-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 flex items-center justify-between">
                                              <div className="flex items-center gap-3 flex-wrap">
                                                <span>Race {raceGroup.race}</span>
                                                <div className="flex flex-wrap gap-2">
                                                  <span className="rounded-full bg-gray-100 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-gray-600">
                                                    {raceHorseCount} horses
                                                  </span>
                                                  {topLexiTips.map((horse) => (
                                                    <span
                                                      key={`${raceGroup.id}-${horse.horseNumber || horse.horseName}`}
                                                      className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 text-[10px] font-semibold text-gray-700 shadow-[0_1px_2px_rgba(0,0,0,0.08)]"
                                                    >
                                                      <img
                                                        src={getSilkSrc(horse)}
                                                        alt="Silk"
                                                        className="h-4 w-4 rounded-full object-contain"
                                                        onError={(e) => {
                                                          if (
                                                            horse?.horseId &&
                                                            horse?.silks &&
                                                            e.target.src.includes("/api/images/silks")
                                                          ) {
                                                            e.target.src = `https://www.puntingform.com.au/images/jockeysilks/${horse.horseId}.jpeg`;
                                                          } else if (
                                                            !e.target.src.includes("silk-fallback.jpg")
                                                          ) {
                                                            e.target.src = "/images/silk-fallback.jpg";
                                                          }
                                                        }}
                                                      />
                                                      <span>#{horse.horseNumber || "-"}</span>
                                                    </span>
                                                  ))}
                                                </div>
                                              </div>
                                              <button
                                                type="button"
                                                onClick={() => toggleRaceCollapse(raceGroup.id)}
                                                className="inline-flex items-center gap-1 text-xs font-semibold text-gray-600 hover:text-gray-800"
                                              >
                                                {isRaceCollapsed ? (
                                                  <>
                                                    <ChevronRight className="w-3.5 h-3.5" aria-hidden="true" />
                                                    Expand R{raceGroup.race}
                                                  </>
                                                ) : (
                                                  <>
                                                    <ChevronDown className="w-3.5 h-3.5" aria-hidden="true" />
                                                    Minimise R{raceGroup.race}
                                                  </>
                                                )}
                                              </button>
                                            </div>
                                            {!isRaceCollapsed && (
                                              <div className="overflow-x-auto">
                                                <table className="min-w-full divide-y divide-gray-100 text-sm">
                                <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                                  <tr>
                                    <th className="px-4 py-2 text-left font-semibold">
                                      <button
                                        type="button"
                                        onClick={() => handleSort("row")}
                                        className="inline-flex items-center gap-1"
                                      >
                                        #
                                        {renderSortIcon("row")}
                                      </button>
                                    </th>
                                    <th className="px-4 py-2 text-left font-semibold">
                                      <button
                                        type="button"
                                        onClick={() => handleSort("date")}
                                        className="inline-flex items-center gap-1"
                                      >
                                        Date
                                        {renderSortIcon("date")}
                                      </button>
                                    </th>
                                    <th className="px-4 py-2 text-left font-semibold">
                                      <button
                                        type="button"
                                        onClick={() => handleSort("track")}
                                        className="inline-flex items-center gap-1"
                                      >
                                        Track
                                        {renderSortIcon("track")}
                                      </button>
                                    </th>
                                    <th className="px-4 py-2 text-left font-semibold">
                                      <button
                                        type="button"
                                        onClick={() => handleSort("race")}
                                        className="inline-flex items-center gap-1"
                                      >
                                        Race
                                        {renderSortIcon("race")}
                                      </button>
                                    </th>
                                    <th className="px-4 py-2 text-left font-semibold">
                                      <button
                                        type="button"
                                        onClick={() => handleSort("horseNumber")}
                                        className="inline-flex items-center gap-1"
                                      >
                                        Horse #
                                        {renderSortIcon("horseNumber")}
                                      </button>
                                    </th>
                                    <th className="px-4 py-2 text-left font-semibold">
                                      <button
                                        type="button"
                                        onClick={() => handleSort("horseName")}
                                        className="inline-flex items-center gap-1"
                                      >
                                        Horse
                                        {renderSortIcon("horseName")}
                                      </button>
                                    </th>
                                    <th className="px-4 py-2 text-left font-semibold">
                                      <button
                                        type="button"
                                        onClick={() => handleSort("odds")}
                                        className="inline-flex items-center gap-1"
                                      >
                                        Odds
                                        {renderSortIcon("odds")}
                                      </button>
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 text-gray-700">
                                  {raceGroup.horses.map((row) => (
                                    <tr
                                      key={row.id}
                                      className={
                                        row.isMentioned ? "bg-[#fff7fc] hover:bg-[#ffeef7]" : "hover:bg-gray-50"
                                      }
                                    >
                                      <td className="px-4 py-2 text-xs text-gray-500">
                                        {row.rowIndex}
                                      </td>
                                      <td className="px-4 py-2">
                                        <div className="font-medium text-gray-800">{row.dayName}</div>
                                        <div className="text-xs text-gray-500">{row.date}</div>
                                      </td>
                                      <td className="px-4 py-2">{row.track}</td>
                                      <td className="px-4 py-2">Race {row.race}</td>
                                      <td className="px-4 py-2">{row.horseNumber || "-"}</td>
                                      <td className="px-4 py-2">
                                        <div className="flex flex-col gap-1">
                                        <div className="group flex items-center gap-2">
                                          <span className="relative inline-flex">
                                            <img
                                              src={getSilkSrc(row)}
                                              alt="Silk"
                                              className="h-6 w-6 rounded-full object-contain"
                                              onError={(e) => {
                                                if (
                                                  row?.horseId &&
                                                  row?.silks &&
                                                  e.target.src.includes("/api/images/silks")
                                                ) {
                                                  e.target.src = `https://www.puntingform.com.au/images/jockeysilks/${row.horseId}.jpeg`;
                                                } else if (
                                                  !e.target.src.includes("silk-fallback.jpg")
                                                ) {
                                                  e.target.src = "/images/silk-fallback.jpg";
                                                }
                                              }}
                                            />
                                            <span className="absolute -bottom-7 left-0 z-10 hidden w-[260px] rounded-md bg-black/80 px-2 py-1 text-[10px] text-white shadow-lg group-hover:inline-flex">
                                              {getSilkDebugInfo(row)}
                                            </span>
                                          </span>
                                          <span className="text-sm font-medium text-gray-800">
                                            {row.horseName || "-"}
                                          </span>
                                        </div>
                                          <div className="flex flex-wrap gap-1">
                                            {row.isLexiProTip && row.lexiProRank && (
                                            <span className="rounded-full bg-[#ffe7f6] px-2.5 py-1 text-[9px] font-semibold uppercase tracking-wide text-brand-pink">
                                                Lexi Pro #{row.lexiProRank}
                                              </span>
                                            )}
                                            {row.isMentioned && (
                                            <span className="rounded-full bg-[#efe9ff] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-purple-600">
                                                Analysis
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      </td>
                                      <td className="px-4 py-2">
                                        {row.odds ? (
                                          <button
                                            type="button"
                                            onClick={() => setShowOddsPercent((prev) => !prev)}
                                            className="flex flex-col text-left"
                                            aria-label="Toggle odds display"
                                          >
                                            <span className="text-sm font-semibold text-gray-800">
                                              {formatOddsDisplay(row.odds, showOddsPercent)}
                                            </span>
                                            <span className="text-xs text-gray-400">
                                              {formatOddsDisplay(row.odds, !showOddsPercent)}
                                            </span>
                                          </button>
                                        ) : (
                                          "-"
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                  <tr className="bg-white">
                                    <td className="px-4 py-3 text-sm text-gray-600" colSpan={7}>
                                      {raceGroup.analysisHtml ? (
                                        <div
                                          className="leading-relaxed text-sm"
                                          dangerouslySetInnerHTML={{ __html: raceGroup.analysisHtml }}
                                        />
                                      ) : (
                                        "No analysis available yet for this race."
                                      )}
                                    </td>
                                  </tr>
                                </tbody>
                                                </table>
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

          </>
        )}
      </div>
    </div>
  );
}
